'use client';

import type { CSSProperties } from 'react';
import classNames from '@wilkins-software/classnames-tailwind-merge';
import { usePathnameData } from './usePathnameData';
import { getIsActive } from './getIsActive';
import { NavDropdownItem } from './navDropdownItem';
import { useNavigation } from './NavigationContext';
import type { NavMenuItem } from './NAV_ITEMS';
import { ContactCtaLink } from '../contact/contact-cta-link.component';

interface NavItemProps {
  item: NavMenuItem;
  idx: number;
}

export function NavItem({ item, idx: index }: NavItemProps) {
  const { isLoaded, pathname, currentUrl } = usePathnameData();
  const { setIsMenuOpen } = useNavigation();

  if ('dropdown' in item && item.dropdown) {
    return (
      <NavDropdownItem text={item.text} dropdown={item.dropdown} idx={index} />
    );
  }

  if (!('href' in item)) {
    return null;
  }

  const currentHash = currentUrl.split('#')[1] || '';
  const currentPathNoHash = currentUrl.split('#')[0] || '';
  const itemPathNoHash = item.href?.split('#')[0] || '';
  const itemHash = item.href?.split('#')[1] || '';

  const isMatchingPath = currentPathNoHash === itemPathNoHash;
  const isMatchingHash = currentHash?.trim()
    ? currentHash?.trim() === itemHash
    : false;
  const isNoHash = currentHash ? !currentHash?.trim() || false : true;
  const isContact = item.href.startsWith('mailto:');

  const navLinkClassName = classNames(
    'nav-link block transition-colors',
    // Mobile kinetic typography
    'max-lg:rounded-[28px] max-lg:border max-lg:border-black/5 max-lg:bg-white/70 max-lg:px-6 max-lg:py-5 max-lg:text-3xl max-lg:font-bold max-lg:leading-none max-lg:tracking-tighter max-lg:text-slate-900 max-lg:shadow-[0_8px_32px_rgba(0,0,0,0.04)] max-lg:backdrop-blur-xl',
    'max-lg:hover:border-primary/20 max-lg:hover:bg-white max-lg:hover:text-primary',
    isContact &&
      'max-lg:border-primary/20 max-lg:bg-primary max-lg:text-white max-lg:shadow-lg max-lg:shadow-primary/20 max-lg:hover:bg-primary max-lg:hover:text-white',
    // Desktop unchanged
    'lg:border-0 lg:bg-transparent lg:p-3 lg:text-xs lg:font-medium lg:uppercase lg:tracking-wide lg:text-inherit lg:shadow-none lg:backdrop-blur-none',
    'lg:hover:bg-transparent lg:hover:text-inherit',
  );

  return (
    <li
      className={classNames('nav-item mobile-nav-item', {
        active: getIsActive({
          isLoaded: isLoaded,
          isMatchingPath: isMatchingPath,
          itemHash: itemHash,
          isMatchingHash: isMatchingHash,
          isNoHash: isNoHash,
        }),
      })}
      data-pathname={pathname}
      style={{ '--mobile-nav-stagger': String(index) } as CSSProperties}
    >
      {item.contactTopic ? (
        <ContactCtaLink
          topicId={item.contactTopic}
          className={navLinkClassName}
          onClick={() => setIsMenuOpen(false)}
        >
          {item.text}
        </ContactCtaLink>
      ) : (
        <a
          className={navLinkClassName}
          href={item.href}
          onClick={() => setIsMenuOpen(false)}
        >
          {item.text}
        </a>
      )}
    </li>
  );
}
